import * as m from 'mithril';
import CustomSvgIcon from '../../../components/CustomSvgIcon';
import { getIntlStringValue } from '../../../helpers/StringHelpers';
import { InternationalStringDTO, OperatorDTO, OperatorRatingSummaryDTO, TripDTO, TripResultDTO, WayPointScheduleDTO } from '../../../apiclient/ApiDtos';
import { MessageProvider } from '../../../context/AppMessages';
import dayjs = require('dayjs');
import { getDayRangeLabel } from '../../../components/TripSearchResultSummaryView';
import { ExpandableTripSummaryComponent } from './ExpandableTripSummaryComponent';
import SearchCriteria from '../../../models/SearchCriteria';

interface TripGroupComponentAttrs {
    messageProvider: MessageProvider,
    searCriteria: SearchCriteria,
    tripGroup: {
        summary: TripResultDTO;
        operator: OperatorDTO;
        busType: InternationalStringDTO;
        trips: Record<WayPointScheduleDTO["scheduledAt"], TripDTO[]>;
    },
    onViewSeatButtonClick: (tripResult: TripResultDTO) => void,
    onViewOperatorPhotosButtonClick: (tripResult: TripResultDTO) => void,
    onRatingSummaryClick: (ratingSummary: OperatorRatingSummaryDTO) => void
}
export function TripGroupComponent(): m.Component<TripGroupComponentAttrs> {
    let wantToExpandAll = false;
    let wantToExpandClickCount = 0;
    return {
        view(vnode) {
            let { tripGroup } = vnode.attrs;
            let currLang = vnode.attrs.messageProvider.getCurrentLang();

            const durations = Object.values(tripGroup.trips)
                .flat()
                .map(t =>
                    dayjs(t.arrival.scheduledAt)
                        .diff(dayjs(t.departure.scheduledAt))
                );

            const hasSameETA = new Set(durations).size == 1;
            const etaMs = dayjs(tripGroup.summary.arrival.scheduledAt).diff(dayjs(tripGroup.summary.departure.scheduledAt));
            const totalMinutes = Math.floor(etaMs / 60000);

            const inHourAndMin = {
                hours: Math.floor(totalMinutes / 60),
                minutes: totalMinutes % 60,
            };

            const trips = Object.values(tripGroup.trips).flat();

            const hasSameUnitPrice =
                trips.length > 0 &&
                trips.every(t =>
                    t.priceRange.min.amount === trips[0].priceRange.min.amount &&
                    t.priceRange.max.amount === trips[0].priceRange.max.amount &&
                    t.priceRange.min.currency === trips[0].priceRange.min.currency
                );

            return m("div.bg-white mb-3", {
                style: "border-radius: 1rem 1rem 0.5rem 0.5rem !important; border-top: 2px solid rgb(183, 207, 229);"
            },
                m("div", [
                    m("div.p-3 shadow-sm_",
                        m("div.d-flex justify-content-between",
                            m("div",
                                m("span", { class: "font-weight-bold text-secondary" }, getIntlStringValue(tripGroup.operator.name, currLang)),
                                m(CustomSvgIcon, {
                                    iconType: 'circle_dot',
                                    themeCssClass: "svg-muted svg-medium"
                                }),
                                m("span", { class: "font-weight-bold text-secondary" }, getIntlStringValue(tripGroup.busType, currLang)),

                                m("div.mt-1",

                                    //vnode.attrs.searCriteria.numberOfSeats == 1 && 
                                    hasSameETA &&
                                    m("div.mt-2 text-muted small",
                                        m(CustomSvgIcon, {
                                            iconType: 'clock_history',
                                            themeCssClass: "svg-muted svg-small"
                                        }),
                                        m("span.mx-2", vnode.attrs.messageProvider.get("trip.eta_duration") + " :"),
                                        inHourAndMin.hours + " Hr ",
                                        inHourAndMin.minutes > 0 && inHourAndMin.minutes + " Min"
                                    ),
                                ),

                                /* (tripGroup.summary.highlights && tripGroup.summary.highlights.length > 0) &&
                                m("div.mt-2", [
                                    tripGroup.summary.highlights.map(hl => {
                                        return m("span.operator-hightlight-tag-service mr-1  mb-1 font-weight-bold text-primary",
                                            getIntlStringValue(hl, vnode.attrs.messageProvider.getCurrentLang())
                                        )
                                    })
                                ]
                                ), */
                            ),
                            m("div.flex-shrink-0 ml-1 text-right",
                                hasSameUnitPrice &&
                                m("div",
                                    m("small", { class: "text-success" }, "MMK "),
                                    m("span.font-weight-bold", { class: "text-success" },
                                        tripGroup.summary.priceRange.min.amount.toLocaleString("en-US"),
                                        (tripGroup.summary.priceRange.max.amount > tripGroup.summary.priceRange.min.amount) ? " +" : ""
                                    ),
                                    m("div.small text-muted_ text-price-total", vnode.attrs.messageProvider.get("common.price_per_seat"))

                                ),
                            )
                        ),

                    ),
                    m("div.p-3 ", {
                        style: "border-top: 1px solid #ecf3fa !important; border-bottom: 1.5px solid rgb(189, 222, 255) !important; box-shadow: 0 0.125rem 0.1rem rgba(0, 0, 0, 0.075) !important;"
                    },
                        m("div.d-flex justify-content-between align-items-center",
                            m("div", {
                                onclick: () => {
                                    wantToExpandClickCount++;
                                    if (wantToExpandClickCount > 1) {
                                        wantToExpandAll = true;
                                    }
                                }
                            },
                                m("img.operator-logo-sm", {
                                    style: {
                                        "max-width": '64px',
                                        "object-fit": "contain"
                                    },
                                    src: tripGroup.operator.logoUrl
                                }),
                            ),
                            m("div.pl-1",

                                tripGroup.summary.isSeatSelectionAllowed &&
                                m("span.operator-tag-pickup-service mr-1  mb-1",
                                    vnode.attrs.messageProvider.get("operator.seat_selectable_tip")
                                ),

                                tripGroup.summary.hasPickUpService && tripGroup.summary.hasDropOffService ?
                                    m("span.operator-tag-pickup-service mr-1 mb-1",
                                        vnode.attrs.messageProvider.get("operator.pickup_dropoff_service_tip")
                                    )

                                    : tripGroup.summary.hasPickUpService ?
                                        m("span.operator-tag-pickup-service mr-1 mb-1",
                                            vnode.attrs.messageProvider.get("operator.pickup_service_only_tip")
                                        )

                                        : tripGroup.summary.hasDropOffService ?
                                            m("span.operator-tag-pickup-service mr-1 mb-1",
                                                vnode.attrs.messageProvider.get("operator.dropoff_service_only_tip")
                                            )

                                            : "",

                                tripGroup.summary.isDirectBookable &&
                                m("span.operator-tag-pickup-service mr-1 mb-1",
                                    vnode.attrs.messageProvider.get("operator.instant_confirmation")
                                ),
                            ),
                            m("div.flex-shrink-1 text-right", {
                                style: "min-width: 80px !important;"
                            },
                                m("span.ml-1",
                                    m("small.text-primary text-right", {
                                        style: "text-decoration:underline;",
                                        onclick: function (event) {
                                            event.preventDefault();
                                            vnode.attrs.onViewOperatorPhotosButtonClick(tripGroup.summary);
                                        }
                                    }, vnode.attrs.messageProvider.get("operator_photos"))
                                ),
                            )
                        )

                    ),
                    Object.entries(tripGroup.trips).map(([scheduledAt, trips]) =>
                        trips.map(trip => {
                            return m("div.border-top", [
                                m(ExpandableTripSummaryComponent, {
                                    isAutoExpanding: wantToExpandAll,
                                    messageProvider: vnode.attrs.messageProvider,
                                    trip: trip,
                                    numberOfSeats: vnode.attrs.searCriteria.numberOfSeats,
                                    showAsMultipleSeatPrice: true,
                                    onViewSeatButtonClick: (result) => {
                                        vnode.attrs.onViewSeatButtonClick(result);
                                    },
                                    onViewOperatorPhotosButtonClick: function (result) {
                                        vnode.attrs.onViewOperatorPhotosButtonClick(result)
                                    }
                                })
                            ])
                        })
                    )
                ])
            )
        }
    }
}